import React, { useState, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Arrow from './svg/Arrow';
import Switch from 'react-switch';
import Close from './svg/Close';

export default function Autocomplete({ options, selected, setSelected, className }: { selected: string[]; setSelected: (selected: string[]) => void; options: { title: string; id: string; }[]; className?: string; }) {
  const [state, setState] = useState({
    input: '',
    show: false,
    displayedOptions: options,
  });

  const baseRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (baseRef.current && !baseRef.current.contains(e.target as Node)) {
        setState(prevState => ({ ...prevState, show: false, input: '', displayedOptions: options }));
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [options]);

  return (
    <div className={twMerge('relative flex flex-col gap-[12px] w-full', className)}>
      <div
        ref={baseRef}
        className='border border-darkBorder bg-gray-0 dark:bg-quinary dark:border-lightBorder pr-[12px] py-[6px] min-h-[40px] w-full flex items-center justify-between gap-[12px] relative rounded-[4px] dark:bg-quinary'
        onClick={() => setState(prevState => ({ ...prevState, show: true }))}
      >
        <input
          className='outline-none border-none bg-transparent flex-1 w-full h-[36px]'
          value={state.input}
          placeholder='ابحث هنا...'
          onChange={({ target: { value } }) => {
            const matchingOptions = options.filter(o => o.title.toLowerCase().includes(value.toLowerCase()));
            setState(prevState => ({ ...prevState, input: value, displayedOptions: matchingOptions, show: true }));
          }}
        />

        <div
          className='grid place-items-center cursor-pointer h-[40px] w-[40px]'
          onClick={(e) => {
            e.stopPropagation();
            setState(prevState => ({ ...prevState, show: !prevState.show }));
          }}
        >
          <Arrow className={twMerge('rotate-0 duration-200 transition-all shrink-0 h-[12px] w-[12px] dark:[&>*]:fill-white' , state.show && 'rotate-180')} />
        </div>

        <div
          ref={menuRef}
          className={twMerge(
            'absolute bg-white dark:bg-quinary z-[5] left-0 bottom-full w-full h-auto max-h-[300px] overflow-y-auto flex-col border border-darkBorder dark:border-lightBorder rounded-b-[4px] p-[12px] hidden shadow-lg',
            state.show && 'flex',
          )}
        >
          {state.displayedOptions.length > 0 ? (
            state.displayedOptions.map(o => {
              const isSelected = selected.includes(o.id);
              return (
                <div
                  key={o.id}
                  className='flex items-center w-full px-[12px] py-[6px] border-b border-b-darkBorder dark:border-b-lightBorder last:border-b-0 justify-between gap-[24px] cursor-pointer hover:bg-gray-0 dark:hover:bg-quaternary'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelected) setSelected(selected.filter(x => x !== o.id));
                    else setSelected([...selected, o.id])
                  }}
                >
                  <span className='text-black dark:text-white'>{o.title}</span>
                  <Switch
                    dir='ltr'
                    width={36}
                    height={20}
                    onColor='#f0f0f0'
                    offColor='#ff00fe'
                    checkedIcon={<></>}
                    uncheckedIcon={<></>}
                    checked={!isSelected}
                    onChange={() => { }}
                  />
                </div>
              );
            })
          ) : (
            <div className='text-center py-2 text-gray-500'>لا توجد نتائج</div>
          )}
        </div>
      </div>

      {selected.length ? (
        <div className='flex gap-[12px] flex-wrap w-full'>
          {options.filter(o => selected.includes(o.id)).map(x => (
            <div className='flex items-center gap-[12px] px-[6px] py-[3px] border border-gray-0 rounded-[4px]'>
              <span>{x.title}</span>
              <Close className='!h-[12px] !w-[12px] cursor-pointer dark:[&>*]:fill-white' onClick={() => setSelected(selected.filter(s => s !== x.id))}/>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}